import { useEffect, useState } from 'react';

import { Modal as ModalAntD, Result, Row } from 'antd';
import { Formik } from 'formik';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

import * as S from './styled';

import {
  getSmartCharging,
  updateSmartCharging,
} from '../../../hooks/smartcharging';
import {
  MODULATION_DYNAMIC,
  MODULATION_STATIC,
  fixedPowerErrors,
  fixedPowerRows,
} from '../../organisms/SmartChargingForm/fixedPower';
import SmartChargingForm from '../../organisms/SmartChargingForm/SmartChargingForm';
import { useSmartChargingStations } from '../../organisms/SmartChargingSimulator/useSmartChargingStations';

const Modal: any = ModalAntD;

export interface SmartChargingEditPageProps {
  smartChargingId: string;
}

const SmartChargingEditPage = ({
  smartChargingId,
}: SmartChargingEditPageProps) => {
  const { pathname }: any = useLocation();

  const [smartCharging, setSmartCharging] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Conectores do grupo para validar a potência fixa (MV2-2005)
  const { chargeBoxes } = useSmartChargingStations({ smartChargingId });
  const [updateLoading, setUpdateLoading] = useState(false);

  const history = useHistory();

  const handleUpdateSmartCharging = async (values: any) => {
    setUpdateLoading(true);

    try {
      await updateSmartCharging(smartCharging.smart_charging_id, {
        ...smartCharging,
        enabled: values.smart_charging.enabled,
        current_nominal_a: values.smart_charging.current_nominal_a,
        algorithm: values.smart_charging.algorithm,
        start_date: values.smart_charging.start_date,
        end_date: values.smart_charging.end_date,
        // MV2-2005: potência fixa não usa medidor; o smartcharging também desvincula
        meter_device_remote_id:
          values.smart_charging.modulation === MODULATION_STATIC
            ? null
            : values.smart_charging?.meter_device?.device_remote_id ||
              values.smart_charging?.meter_device_remote_id,
        meter_device_remote_in_circuit:
          values.smart_charging?.meter_device_remote_in_circuit,
        modulation: values.smart_charging.modulation || MODULATION_DYNAMIC,
        connector_powers: values.smart_charging.connector_powers || [],
      });

      setSuccess(true);
    } catch (e: any) {
      setErrorMessage(e?.response?.data?.message || null);
      setTimeout(() => setError(true), 1500);
    } finally {
      setTimeout(() => setUpdateLoading(false), 1500);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const { data } = await getSmartCharging(smartChargingId);

        setSmartCharging(data);
      } finally {
        setTimeout(() => setLoading(false), 2000);
      }
    })();
  }, [pathname, smartChargingId]);

  if (!smartCharging) {
    return null;
  }

  return (
    <Formik
      validateOnMount
      initialValues={{ smart_charging: smartCharging }}
      validate={(values) => {
        const errors: any = {};
        let hasError = false;
        if (values.smart_charging.enabled) {
          errors.smart_charging = {};
          if (!values.smart_charging.start_date) {
            hasError = true;
            errors.smart_charging.start_date =
              'A data de início é um campo obrigatório';
          }
          if (!values.smart_charging.end_date) {
            hasError = true;
            errors.smart_charging.end_date =
              'A data de término é um campo obrigatório';
          }
          if (
            moment(values.smart_charging.start_date).isAfter(
              values.smart_charging.end_date,
            )
          ) {
            hasError = true;
            errors.smart_charging.start_date =
              'A data de início deve ser anterior  a data final';
          }
          if (!values.smart_charging.current_nominal_a) {
            hasError = true;
            errors.smart_charging.current_nominal_a =
              'A corrente nominal do disjuntor (a) é um campo obrigatório';
          }
          if (!values.smart_charging.algorithm) {
            hasError = true;
            errors.smart_charging.algorithm =
              'O algoritmo é um campo obrigatório';
          }
          if (values.smart_charging.modulation === MODULATION_STATIC) {
            const powerErrors = fixedPowerErrors(
              values.smart_charging.algorithm,
              values.smart_charging.current_nominal_a,
              fixedPowerRows(chargeBoxes),
              values.smart_charging.connector_powers,
            );
            if (powerErrors.length > 0) {
              hasError = true;
              errors.smart_charging.connector_powers = `Potência fixa: ${powerErrors.join(
                '; ',
              )}`;
            }
          }
        }
        return hasError ? errors : {};
      }}
      onSubmit={handleUpdateSmartCharging}
    >
      {({ handleSubmit }) => {
        return (
          <S.Wrapper>
            <SmartChargingForm loading={loading} />

            <Row style={{ marginTop: 32, justifyContent: 'space-between' }}>
              <S.Button
                disabled={loading}
                size='large'
                danger
                onClick={history.goBack}
              >
                Cancelar
              </S.Button>

              <S.Button
                loading={loading || updateLoading}
                key='submit'
                size='large'
                type='primary'
                onClick={() => handleSubmit()}
              >
                Editar smart charging
              </S.Button>
            </Row>

            <Modal
              visible={success}
              onOk={() => {}}
              onCancel={() => {}}
              closable={false}
              footer={null}
            >
              <Result
                status='success'
                title='Smart Charging atualizado com sucesso!'
                extra={[
                  <S.Button
                    type='primary'
                    key='console'
                    size='large'
                    onClick={() => setSuccess(false)}
                  >
                    Voltar para a visualização da configuração
                  </S.Button>,
                ]}
              />
            </Modal>

            <Modal visible={error} closable={false} footer={null}>
              <Result
                status='error'
                title='Não foi possível atualizar o Smart Charging!'
                subTitle={errorMessage || undefined}
                extra={[
                  <S.Button
                    type='primary'
                    key='default'
                    size='large'
                    onClick={() => setError(false)}
                    style={{ marginTop: 16 }}
                  >
                    Voltar para a visualização do grupo
                  </S.Button>,
                ]}
              />
            </Modal>
          </S.Wrapper>
        );
      }}
    </Formik>
  );
};

export default SmartChargingEditPage;
